import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CustomerServiceIssueType } from '../../../../core/models/customer-service/customer-service-issue-type.model';
import { CustomerServiceIssueTypeMapper } from '../../../../core/mappers/customer-service/mapper-customer-service-issue-type.service';
import { ApiCustomerServiceIssueTypeService } from '../../../../core/services/api/customer-service/api-customer-service-issue-type.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalCustomerServiceIssueTypeComponent } from '../../modals/customer-services/modal-customer-service-issue-type/modal-customer-service-issue-type.component';

@Component({
  selector: 'app-customer-service-issue-type',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    ModalCustomerServiceIssueTypeComponent
  ],
  templateUrl: './customer-service-issue-type.component.html',
  styleUrl: './customer-service-issue-type.component.css'
})
export class CustomerServiceIssueTypeComponent implements OnInit {
  private readonly apiService = inject(ApiCustomerServiceIssueTypeService);
  private readonly mapper = inject(CustomerServiceIssueTypeMapper);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  // ================= State Variables =================
  isLoading: boolean = false;
  issueTypes: CustomerServiceIssueType[] = [];

  // --- Filtering & Searching ---
  searchText: string = '';
  searchField: string = 'all';
  isSearchTypeDropdownOpen: boolean = false;
  activeFilter: boolean | null = null;
  isActiveFilterDropdownOpen: boolean = false;

  // --- Sorting ---
  sortColumn: string = 'sortOrder';
  sortDirection: 'asc' | 'desc' | '' = 'asc';

  // --- Pagination ---
  currentPage: number = 1;
  pageSize: number = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems: number = 0;
  totalPages: number = 0;
  isPageSizeDropdownOpen: boolean = false;

  // --- Modal Forms ---
  isModalOpen: boolean = false;
  selectedIssueType: CustomerServiceIssueType | null = null;

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  // ================= Normal Functions =================

  ngOnInit(): void {
    this.loadIssueTypes();
  }

  /**
   * Reset pagination and load table data
   */
  onSearchChange(): void {
    this.currentPage = 1;
    this.loadIssueTypes();
  }

  /**
   * Toggle search parameter dropdown options list
   */
  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  /**
   * Select a search parameter key column target
   */
  selectSearchType(type: string): void {
    this.searchField = type;
    this.isSearchTypeDropdownOpen = false;
    this.onSearchChange();
  }

  /**
   * Translates active search parameter key into readable Thai label
   */
  get currentSearchTypeLabel(): string {
    switch (this.searchField) {
      case 'code': return 'รหัสประเภทเรื่อง';
      case 'name': return 'ชื่อประเภทเรื่อง';
      default: return 'ทั้งหมด';
    }
  }

  /**
   * Translates active filter value into readable Thai label
   */
  get activeFilterLabel(): string {
    if (this.activeFilter === true) return 'เปิดใช้งาน';
    if (this.activeFilter === false) return 'ปิดใช้งาน';
    return 'ทั้งหมด';
  }

  /**
   * Toggle active state dropdown options list
   */
  toggleActiveFilterDropdown(): void {
    this.isActiveFilterDropdownOpen = !this.isActiveFilterDropdownOpen;
  }

  /**
   * Safely dismiss active state dropdown list
   */
  closeActiveFilterDropdown(): void {
    setTimeout(() => {
      this.isActiveFilterDropdownOpen = false;
    }, 200);
  }

  /**
   * Update selected active filter value and reload table list
   */
  selectActiveFilter(val: boolean | null): void {
    this.activeFilter = val;
    this.isActiveFilterDropdownOpen = false;
    this.onSearchChange();
  }

  /**
   * Order table rows by column key and toggle direction
   */
  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadIssueTypes();
  }

  /**
   * Toggle page size selection options list
   */
  togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  /**
   * Safely dismiss page size selection options list
   */
  closePageSizeDropdown(): void {
    setTimeout(() => {
      this.isPageSizeDropdownOpen = false;
    }, 200);
  }

  /**
   * Select a page size limit for the table display rows
   */
  selectPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.isPageSizeDropdownOpen = false;
    this.loadIssueTypes();
  }

  /**
   * Navigate to target page of issue type records
   */
  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadIssueTypes();
    }
  }

  /**
   * Starting index number of current page items display range
   */
  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  /**
   * Ending index number of current page items display range
   */
  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  /**
   * Numeric page index options array list to render in table footer
   */
  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  /**
   * Translate Category API codes to Thai
   */
  getCategoryLabel(category: string): string {
    return this.mapper.translateCategory(category);
  }

  /**
   * Translate Resolution Mode API codes to Thai
   */
  getResolutionModeLabel(mode: string): string {
    return this.mapper.translateResolutionMode(mode);
  }

  /**
   * Open modal popup to create a new Issue Type configuration
   */
  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้างประเภทเรื่องปัญหา');
      return;
    }
    this.selectedIssueType = null;
    this.isModalOpen = true;
  }

  /**
   * Open modal popup to update an existing Issue Type configurations
   */
  openEdit(item: CustomerServiceIssueType): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไขข้อมูล');
      return;
    }
    this.selectedIssueType = item;
    this.isModalOpen = true;
  }

  /**
   * Refresh table view upon modal save actions
   */
  onSaved(): void {
    this.loadIssueTypes();
  }

  // ================= API Functions =================

  /**
   * Fetch issue type records matching queries from API endpoint
   */
  loadIssueTypes(): void {
    this.isLoading = true;

    const query = {
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.searchText.trim() ? this.searchField : undefined,
      searchText: this.searchText.trim() || undefined,
      active: this.activeFilter !== null ? this.activeFilter : undefined
    };

    this.apiService.getIssueTypeForTable(query).subscribe({
      next: (res) => {
        console.log('getIssueTypeForTable', res);
        this.isLoading = false;
        const mapped = this.mapper.mapIssueTypeTable(res);

        // Sorting local fallback (if not handled by API query)
        if (this.sortColumn) {
          mapped.data.sort((a: any, b: any) => {
            const valA = a[this.sortColumn];
            const valB = b[this.sortColumn];
            if (valA == null) return 1;
            if (valB == null) return -1;

            if (typeof valA === 'string') {
              return this.sortDirection === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
            } else {
              return this.sortDirection === 'asc'
                ? valA - valB
                : valB - valA;
            }
          });
        }

        this.issueTypes = mapped.data;
        this.totalItems = mapped.total;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดข้อมูลประเภทเรื่องปัญหาได้');
        console.error(err);
        this.issueTypes = [];
      }
    });
  }
}
